import { Transaction } from '@/types/transaction';

/**
 * Interface for recurrence sequence information
 */
export interface RecurrenceSequence {
  current: number;
  total?: number;
  display: string;
}

/**
 * Calculate recurrence sequence information for a transaction
 * @param transaction - The transaction to calculate sequence for
 * @param allTransactions - Optional array of all transactions to help calculate position
 * @returns RecurrenceSequence object or null if not a recurring transaction
 */
export function getRecurrenceSequence(
  transaction: Transaction,
  allTransactions?: Transaction[]
): RecurrenceSequence | null {
  // Don't show sequence for non-recurring transactions
  if (!transaction.is_recurring) {
    return null;
  }

  let current: number;
  let total: number | undefined;

  // For parent transactions (no parent_transaction_id), show as first in series
  if (!transaction.parent_transaction_id) {
    current = 1;
    total = transaction.recurrence_count || undefined;
  } else {
    // For child transactions, try multiple approaches to get current position

    // 1. Use recurrence_created_count if available and > 0
    if (transaction.recurrence_created_count && transaction.recurrence_created_count > 0) {
      current = transaction.recurrence_created_count;
    } else if (allTransactions) {
      // 2. Calculate position by finding transactions in the same recurring series
      // Multiple grouping strategies for better accuracy

      let siblingTransactions: Transaction[] = [];

      // Strategy 1: Group by series including the parent and all children
      if (transaction.parent_transaction_id) {
        const parentId = transaction.parent_transaction_id;
        siblingTransactions = allTransactions.filter(t =>
          t.id === parentId || t.parent_transaction_id === parentId
        );
      }

      // Strategy 2: If no parent found, group by description and pattern
      if (siblingTransactions.length === 0) {
        siblingTransactions = allTransactions.filter(t =>
          t.description === transaction.description &&
          t.is_recurring &&
          t.recurrence_pattern === transaction.recurrence_pattern &&
          t.recurrence_interval === transaction.recurrence_interval
        );
      }

      // Strategy 3: If still no matches, group just by description for recurring transactions
      if (siblingTransactions.length === 0) {
        siblingTransactions = allTransactions.filter(t =>
          t.description === transaction.description &&
          t.is_recurring
        );
      }

      // Sort by date (fallback created_at)
      siblingTransactions.sort((a, b) => {
        const aTime = new Date(a.date as any).getTime() || new Date((a as any).created_at || 0).getTime();
        const bTime = new Date(b.date as any).getTime() || new Date((b as any).created_at || 0).getTime();
        if (aTime !== bTime) return aTime - bTime;
        // Stable fallback by id string
        return String(a.id).localeCompare(String(b.id));
      });

      const position = siblingTransactions.findIndex(t => t.id === transaction.id);
      if (position >= 0) {
        current = position + 1; // +1 because positions are 0-indexed
      } else {
        current = 1;
      }

      // Enhanced total detection
      // First try: parent transaction in the same group
      const parentTransaction = siblingTransactions.find(t => !t.parent_transaction_id);
      if (parentTransaction && parentTransaction.recurrence_count) {
        total = parentTransaction.recurrence_count;
      } else {
        // Second try: any transaction in the group with recurrence_count
        const transactionWithCount = siblingTransactions.find(t => t.recurrence_count);
        if (transactionWithCount && transactionWithCount.recurrence_count) {
          total = transactionWithCount.recurrence_count;
        } else {
          // Third try: any transaction in ALL transactions with same description
          const anyTransactionWithCount = allTransactions.find(t =>
            t.description === transaction.description &&
            t.is_recurring &&
            t.recurrence_count
          );
          if (anyTransactionWithCount && anyTransactionWithCount.recurrence_count) {
            total = anyTransactionWithCount.recurrence_count;
          }
        }
      }
    } else {
      // 3. Default to 1 if we can't calculate position
      current = 1;
    }

    // Use transaction's own recurrence_count as last resort
    if (!total) {
      total = transaction.recurrence_count || undefined;
    }
  }

  // Ensure we have valid values
  if (current <= 0) {
    current = 1;
  }

  return {
    current,
    total,
    display: formatSequenceDisplay(current, total)
  };
}

/**
 * Format sequence display string
 * @param current - Current occurrence number
 * @param total - Total planned occurrences (optional)
 * @returns Formatted display string
 */
export function formatSequenceDisplay(current: number, total?: number): string {
  if (!total || total <= 0) {
    // Unlimited recurrence - show current only or with infinity symbol
    return `(${current}-∞)`;
  }

  if (total <= 1) {
    // Single occurrence - no need to show sequence
    return '';
  }

  return `(${current}-${total})`;
}

/**
 * Format sequence display for mobile (more compact)
 * @param current - Current occurrence number
 * @param total - Total planned occurrences (optional)
 * @returns Formatted mobile display string
 */
export function formatSequenceDisplayMobile(current: number, total?: number): string {
  if (!total || total <= 0) {
    return `(${current})`;
  }

  if (total <= 1) {
    return '';
  }

  return `(${current}/${total})`;
}

/**
 * Check if a transaction should display sequence information
 * @param transaction - The transaction to check
 * @param allTransactions - Optional array of all transactions for better position calculation
 * @returns True if sequence should be displayed
 */
export function shouldDisplaySequence(
  transaction: Transaction,
  allTransactions?: Transaction[]
): boolean {
  if (!transaction.is_recurring) {
    return false;
  }

  const sequence = getRecurrenceSequence(transaction, allTransactions);
  return !!sequence && sequence.display !== '';
}

/**
 * Get accessible text for screen readers
 * @param transaction - The transaction
 * @returns Accessible text in Portuguese
 */
export function getAccessibleSequenceText(transaction: Transaction): string {
  const sequence = getRecurrenceSequence(transaction);

  if (!sequence) {
    return '';
  }

  if (!sequence.total) {
    return `ocorrência ${sequence.current} de série recorrente ilimitada`;
  }

  return `ocorrência ${sequence.current} de ${sequence.total}`;
}